/**
 * Format money in a human-readable format.
 *
 * @param moneySpentStreaming - Subscription royalties in user's currency (e.g., 12.50 = £12.50)
 * @param moneySpentD2C - D2C purchases in user's currency (e.g., 12.50 = £12.50)
 * @param currencySymbol - Currency symbol to use (defaults to £)
 * @returns Formatted money string
 */
export const formatMoneySpent = (
  moneySpentStreaming: number,
  moneySpentD2C: number,
  currencySymbol: string = "£",
): string => {
  const total = moneySpentStreaming + moneySpentD2C;
  return formatMoney(total, currencySymbol);
};

/**
 * Format a money value in a human-readable format.
 *
 * @param amount - Amount in currency units (e.g., 12.50)
 * @param currencySymbol - Currency symbol to use (defaults to £)
 * @returns Formatted money string
 */
export const formatMoney = (
  amount: number,
  currencySymbol: string = "£",
): string => {
  if (amount >= 1000) {
    return `${currencySymbol}${(amount / 1000).toFixed(1)}k`;
  }
  if (amount >= 100) {
    return `${currencySymbol}${Math.round(amount)}`;
  }
  if (amount >= 1) {
    return `${currencySymbol}${amount.toFixed(2)}`;
  }
  if (amount >= 0.01) {
    return `${currencySymbol}${amount.toFixed(2)}`;
  }
  if (amount > 0) {
    // Amount is greater than 0 but less than 0.01
    return `< ${currencySymbol}0.01`;
  }
  return `${currencySymbol}0`;
};

/**
 * Format streaming money only (no D2C conversion needed).
 *
 * @param moneySpentStreaming - Subscription royalties in user's currency
 * @param currencySymbol - Currency symbol to use (defaults to £)
 * @returns Formatted money string
 */
export const formatStreamingMoney = (
  moneySpentStreaming: number,
  currencySymbol: string = "£",
): string => {
  return formatMoney(moneySpentStreaming, currencySymbol);
};
